import emailjs from '@emailjs/browser';

// --- VIKTIG KONFIGURASJON ---
// Disse ID-ene må matche det du oppretter inne på emailjs.com under "Email Templates".
const TEMPLATE_IDS = {
  welcome: 'template_welcome',           // Velkomstmail til nye kunder/brukere
  newReport: 'template_new_report',      // Varsel til bedriften om ny sak
  receiptReporter: 'template_receipt',   // Kvittering til varsleren
  bookingAlert: 'template_booking',      // Varsel til selger om nytt møte
  passwordReset: 'template_reset',       // Glemt passord
  hmsIncident: 'template_hms',           // HMS hendelse kvittering
  
  // -- NYE MALER --
  inviteUser: 'template_invite',         // Invitasjon til kollega
  investigationAssigned: 'template_investigation_assigned', // Tildeling av sak
  deadlineReminder: 'template_reminder_deadline', // Fristvarsel
  subscription: 'template_company_registration'   // Abonnementsbekreftelse
};

class EmailService {
  constructor() {
    this.isConfigured = false;
    this.serviceId = null;
    this.publicKey = null;
  }

  // Kalles fra EmailContext når appen starter eller innstillinger endres
  updateConfiguration(serviceId, publicKey) {
    if (serviceId && publicKey) {
      this.serviceId = serviceId;
      this.publicKey = publicKey;
      try {
        emailjs.init(publicKey);
        this.isConfigured = true;
        console.log('✅ EmailJS er koblet til med Service ID:', serviceId);
      } catch (error) {
        console.error('❌ Feil ved initialisering av EmailJS:', error);
        this.isConfigured = false;
      }
    }
  }

  async sendEmail(templateId, templateParams, userEmail = null) {
    if (!this.isConfigured) {
      console.warn('⚠️ E-post ikke sendt: EmailJS mangler konfigurasjon (Service ID / Public Key).');
      return { success: false, error: 'Mangler konfigurasjon' };
    }

    // Sikre at vi har en mottaker
    const recipient = userEmail || templateParams.to_email;
    if (!recipient) {
      console.error('❌ E-post feilet: Ingen mottaker (to_email) spesifisert.');
      return { success: false, error: 'Ingen mottaker' };
    }

    // Legg til standardparametre
    const finalParams = {
      ...templateParams,
      to_email: recipient,
      system_name: 'HMSvarsling',
      support_email: 'support@hmsvarsling.no',
      year: new Date().getFullYear()
    };

    console.log(`📧 Sender e-post (${templateId}) til: ${recipient}...`, finalParams);

    try {
      const response = await emailjs.send(
        this.serviceId,
        templateId,
        finalParams
      );
      console.log('✅ E-post sendt!', response.status, response.text);
      return { success: true, response };
    } catch (error) {
      console.error('❌ E-post feilet:', error);
      return { success: false, error: error.text || error.message };
    }
  }

  // Metode for batch-sending (brukes av kø-systemet)
  async sendBatchEmails(emailQueue) {
    const results = [];
    for (const email of emailQueue) {
      const result = await this.sendEmail(email.templateId, email.templateParams, email.userEmail);
      results.push({ ...email, result });
    }
    return results;
  }

  // --- 1. VELKOMST E-POST ---
  async sendWelcomeEmail(userEmail, userName, userType, companyName) {
    const params = {
      user_name: userName,
      user_type: userType === 'company' ? 'Bedriftsleder' : 'Bruker',
      company_name: companyName,
      login_url: `${window.location.origin}/#/${userType}/login`
    };
    return await this.sendEmail(TEMPLATE_IDS.welcome, params, userEmail);
  }

  // --- 2. NYTT VARSEL (Til Bedriften) ---
  async sendNewReportNotification(companyEmail, reportId, reportType, companyName) {
    const typeText = reportType === 'hms_incident' ? 'HMS-hendelse' : 'Varslingssak (RUH)';
    const params = {
      company_name: companyName,
      report_type: typeText,
      report_id: reportId,
      report_url: `${window.location.origin}/#/company/report/${reportId}`
    };
    return await this.sendEmail(TEMPLATE_IDS.newReport, params, companyEmail);
  }

  // --- 3. KVITTERING TIL VARSLER ---
  async sendReceiptToReporter(reporterEmail, reportId, companyName) {
    const params = {
      company_name: companyName,
      report_id: reportId,
      login_url: `${window.location.origin}/#/whistleblower/return-login`
    };
    return await this.sendEmail(TEMPLATE_IDS.receiptReporter, params, reporterEmail);
  }

  // --- 4. BOOKING / MØTE (Til Selger) ---
  async sendBookingNotification(sellerEmail, bookingData) {
    const params = {
      customer_name: bookingData.customerName,
      customer_email: bookingData.customerEmail,
      customer_company: bookingData.customerCompany || 'Ikke oppgitt',
      booking_date: new Date(bookingData.date).toLocaleDateString('no-NO'),
      booking_time: bookingData.time,
      notes: bookingData.notes || 'Ingen notater'
    };
    return await this.sendEmail(TEMPLATE_IDS.bookingAlert, params, sellerEmail);
  }

  // --- 5. PASSORD RESET ---
  async sendPasswordResetEmail(userEmail, resetToken, userName) {
    const params = {
      user_name: userName || 'Bruker',
      reset_url: `${window.location.origin}/#/reset-password?token=${resetToken}`
    };
    return await this.sendEmail(TEMPLATE_IDS.passwordReset, params, userEmail);
  }
}

const emailService = new EmailService();
export const initializeEmailService = () => {
  // Denne kjøres automatisk fra EmailContext
};

export default emailService;